/**
 * Position Helpers
 * 
 * Utility functions for position manipulation and calculations.
 * Extracted from StrategyWizardDialog.tsx for better maintainability.
 */

import { Leg, ExpiryType, SelectionMethod, StrategyConfig } from '../../types/strategy';
import { createDefaultPositionTemplate, DEFAULT_POSITIONAL_VALUES, EXPIRY_TYPE, SLIDER_RANGE } from './strategyConstants';

/**
 * Map of strike-selection method → the Leg fields that method owns.
 *
 * Used by `computeStrategySignature` to normalize non-current-method fields
 * out of the fingerprint (so a leg's dirty-state reflects only the fields the
 * current method uses, not stale values left over from a method-switch
 * round-trip).
 *
 * `strikePrice` is meaningful only for ATM_POINTS / ATM_PERCENT (user picks
 * OTM3 / ATM+5.00% / etc.). For every other method it's a UI sentinel
 * ('DYNAMIC' from `transformToFrontend`, 'ATM' from method-switch defaults)
 * and must not participate in the signature.
 *
 * NOTE: LegMatrix's method-switch onChange does NOT drive from this map —
 * it intentionally preserves stale fields across dropdown misclicks so
 * users don't lose their PREMIUM value when flipping through methods. The
 * signature normalize above compensates: stale fields exist on the leg but
 * don't affect the dirty-state fingerprint.
 */
export const METHOD_OWNED_FIELDS: Record<SelectionMethod, (keyof Leg)[]> = {
  ATM_POINTS: ['strikePrice'],
  ATM_PERCENT: ['strikePrice'],
  PREMIUM: ['premiumOperator', 'premiumValue'],
  PERCENTAGE_OF_STRADDLE_PREMIUM: ['straddlePremiumOperator', 'straddlePremiumPercentage'],
  STRADDLE_WIDTH: ['straddleWidth'],
  PERCENTAGE_OF_UNDERLYING: ['underlyingPremiumOperator', 'underlyingPercentage'],
};

// Stable ID counter for better performance
let legIdCounter = 0;

/**
 * Generate stable ID for new positions
 */
export const generateStableId = (): string => `leg-${++legIdCounter}-${Date.now()}`;

/**
 * Create a new position with default values
 */
export const createNewPosition = (): Leg => {
  const template = createDefaultPositionTemplate();
  return {
    id: generateStableId(),
    ...template
  };
};

/**
 * Clone an existing position with new ID.
 *
 * A clone is always an independent MAIN leg — the hedge linkage is intentionally
 * dropped so a copy can never become a second hedge pointing at the same main
 * (the backend enforces one-hedge-per-main). Copy is also hidden on hedge rows
 * in the builder; this strip is the belt-and-braces guard.
 */
/**
 * Whether a MAIN leg is eligible for the associate-hedge recycle opt-in — i.e. it has any
 * EARLY-EXIT-or-re-entry trigger: stop-loss, trailing-stop-loss, target-profit, re-entry, or
 * re-execute. The single source of truth for the recycle opt-in: the builder shows the Recycle
 * toggle only when this is true, the recycle-clear cascade fires when it becomes false, and
 * `validateHedgeLinks` requires it. Mirrors the backend `validate_hedge_legs` gate
 * (`_RECYCLE_ELIGIBLE_MAIN_FEATURES`). A main with none of these only exits at strategy-exit,
 * where the base main-first ordering already releases the hedge — recycle would add nothing.
 * (Not `waitAndTrade` — that gates ENTRY, not an early exit.)
 *
 * NOTE: this looks like `hasEnabledRiskManagement` below, but the two answer different
 * questions and only overlap by coincidence — this list intentionally EXCLUDES
 * `waitAndTrade` (which recycle must never allow) and takes an optional `main`. Keep them
 * independent; do NOT derive one from the other.
 */
export const mainRecycleEligible = (main?: Leg): boolean =>
  !!(main?.stopLoss?.enabled || main?.trailingStopLoss?.enabled || main?.targetProfit?.enabled ||
    main?.reEntry?.enabled || main?.reExecute?.enabled);

export const clonePosition = (position: Leg): Leg => {
  const { isHedge: _isHedge, hedgeForLegId: _hedgeForLegId, hedgeRecycle: _hedgeRecycle, ...rest } = position;
  return {
    ...rest,
    id: generateStableId()
  };
};

/**
 * Update a specific position in an array
 */
export const updatePositionInArray = (
  positions: Leg[], 
  positionId: string, 
  updates: Partial<Leg>
): Leg[] => {
  return positions.map(position => 
    position.id === positionId ? { ...position, ...updates } : position
  );
};

/**
 * Remove a position from an array
 */
export const removePositionFromArray = (
  positions: Leg[], 
  positionId: string
): Leg[] => {
  return positions.filter(position => position.id !== positionId);
};

// updateAllPositionsIndex function removed - index is now strategy-level only

/**
 * Get expiry text from strategy config
 * Returns the strategy-level expiry type
 */
export const getExpiryText = (expiryType: ExpiryType): ExpiryType => {
  return expiryType;
};

/**
 * Get maximum slider range based on expiry type
 */
export const getMaxSliderRange = (expiryType: ExpiryType): number => {
  return expiryType === EXPIRY_TYPE.MONTHLY ? SLIDER_RANGE.MONTHLY_MAX : SLIDER_RANGE.WEEKLY_MAX;
};

/**
 * Get default values for positional sliders
 */
export const getDefaultPositionalValues = () => DEFAULT_POSITIONAL_VALUES;

/**
 * Check if position has enabled risk management
 */
export const hasEnabledRiskManagement = (position: Leg): boolean => {
  return (position.stopLoss?.enabled || false) ||
         (position.targetProfit?.enabled || false) ||
         (position.trailingStopLoss?.enabled || false) ||
         (position.waitAndTrade?.enabled || false) ||
         (position.reEntry?.enabled || false) ||
         (position.reExecute?.enabled || false);
};

/**
 * Count positions with specific risk management enabled
 */
export const countPositionsWithRiskType = (
  positions: Leg[], 
  riskType: keyof Pick<Leg, 'stopLoss' | 'targetProfit' | 'trailingStopLoss' | 'waitAndTrade' | 'reEntry' | 'reExecute'>
): number => {
  return positions.filter(position => position[riskType].enabled).length;
};

/**
 * Get position summary statistics
 */
export const getPositionSummary = (positions: Leg[]) => {
  return {
    total: positions.length,
    buyPositions: positions.filter(p => p.actionType === 'BUY').length,
    sellPositions: positions.filter(p => p.actionType === 'SELL').length,
    callPositions: positions.filter(p => p.optionType === 'CE').length,
    putPositions: positions.filter(p => p.optionType === 'PE').length,
    totalLots: positions.reduce((sum, p) => sum + p.totalLots, 0),
    withStopLoss: countPositionsWithRiskType(positions, 'stopLoss'),
    withTargetProfit: countPositionsWithRiskType(positions, 'targetProfit'),
    withTrailingStopLoss: countPositionsWithRiskType(positions, 'trailingStopLoss')
  };
};

/**
 * Buy/sell exposure of a leg list, case-insensitive on actionType.
 *
 * Single source of truth for the leg-aware Combined-Premium % MTM cap: a LOSS can
 * exceed 100% of the entry net premium only when the book has short exposure (any
 * SELL leg) — a long-only book's max loss is the premium paid; a PROFIT can exceed
 * 100% only when it has a long (BUY) leg. Mirrors the backend `_leg_exposure`.
 * A protective hedge is a BUY leg, so a hedged short still reports hasSellLeg.
 */
export const legExposure = (legs: Leg[] | undefined): { hasBuyLeg: boolean; hasSellLeg: boolean } => {
  const acts = new Set((legs || []).map(l => (l.actionType || '').toUpperCase()));
  return { hasBuyLeg: acts.has('BUY'), hasSellLeg: acts.has('SELL') };
};

/**
 * Validate position interdependencies
 */
export const validatePositionInterdependencies = (position: Leg): string[] => {
  const errors: string[] = [];

  // Trailing stop loss requires stop loss to be enabled
  if ((position.trailingStopLoss?.enabled || false) && !(position.stopLoss?.enabled || false)) {
    errors.push('Trailing stop loss requires stop loss to be enabled');
  }

  return errors;
};

/**
 * Auto-correct position interdependencies
 */
export const autoCorrectPositionInterdependencies = (position: Leg): Leg => {
  const correctedPosition = { ...position };

  // Provide default values if risk management objects are undefined
  if (!correctedPosition.stopLoss) {
    correctedPosition.stopLoss = { enabled: false, type: 'POINTS', value: 0 };
  }
  if (!correctedPosition.trailingStopLoss) {
    correctedPosition.trailingStopLoss = { enabled: false, type: 'POINTS', instrumentMoveValue: 0, stopLossMoveValue: 0 };
  }
  if (!correctedPosition.targetProfit) {
    correctedPosition.targetProfit = { enabled: false, type: 'POINTS', value: 0 };
  }
  if (!correctedPosition.waitAndTrade) {
    // 5 keys — matches transformToFrontend seed shape so signature stays
    // stable across a leg that goes through this default vs one loaded from
    // backend. Phase 6 review-fix M3: adding `tradeOnlyFirstEntry: false`
    // prevents phantom dirty-state on new legs vs backend-loaded legs.
    correctedPosition.waitAndTrade = { enabled: false, type: 'POINTS', value: 0, continueOvernight: false, tradeOnlyFirstEntry: false };
  }
  if (!correctedPosition.reEntry) {
    correctedPosition.reEntry = { enabled: false, type: 'SL_REENTRY', count: 1 };
  }
  if (!correctedPosition.reExecute) {
    correctedPosition.reExecute = { enabled: false, type: 'TP_REEXEC', count: 1 };
  }

  // Disable trailing stop loss if stop loss is disabled
  if (!correctedPosition.stopLoss.enabled && correctedPosition.trailingStopLoss.enabled) {
    correctedPosition.trailingStopLoss.enabled = false;
  }

  return correctedPosition;
};

/**
 * Canonical, value-based fingerprint of the editable strategy form.
 *
 * Two form states produce an identical signature iff they would persist the
 * same strategy. Used for unsaved-edit (dirty) detection: compare a baseline
 * signature (captured on load) against the live signature.
 *
 * Legs are run through `autoCorrectPositionInterdependencies` first because the
 * position layer auto-corrects every edited leg (adding default SL/TP/WAT/
 * re-entry objects). Normalizing BOTH the baseline and the live legs the same
 * way means a net-zero round-trip edit (e.g. toggling a leg CE → PE → CE)
 * compares equal to the untouched baseline instead of looking "dirty".
 *
 * Leg `id` is intentionally excluded — it is a transient client-generated key,
 * not persisted state. Add/remove/copy/reorder still change the array contents
 * (and thus the signature), so structural edits remain detected.
 */
export const computeStrategySignature = (
  strategyName: string,
  strategyIndex: string,
  strategyConfig: StrategyConfig,
  legs: Leg[]
): string =>
  JSON.stringify({
    name: (strategyName || '').trim(),
    index: strategyIndex,
    config: strategyConfig,
    legs: legs.map((leg) => {
      const n = autoCorrectPositionInterdependencies(leg);
      // Hedge linkage is fingerprinted by the TARGET main leg's array position
      // (stable), not its transient `id` (which, like leg `id`, is excluded).
      const hedgeForLegIndex = n.isHedge
        ? legs.findIndex((l) => l.id === n.hedgeForLegId)
        : -1;
      // Method-specific fields are included only when the current method OWNS
      // them (per METHOD_OWNED_FIELDS). Without this, a freshly-created leg
      // (which seeds ALL method defaults) and a leg loaded from DB (which only
      // carries the current method's fields) would fingerprint differently for
      // the same persisted state — see the strategySignature test suite.
      const method = n.selectionMethod;
      const owned = new Set(METHOD_OWNED_FIELDS[method] ?? []);
      const pick = <K extends keyof Leg>(k: K): Leg[K] | undefined =>
        (owned.has(k) ? n[k] : undefined);
      return {
        optionType: n.optionType,
        actionType: n.actionType,
        strikePrice: pick('strikePrice'),
        totalLots: n.totalLots,
        selectionMethod: method,
        premiumOperator: pick('premiumOperator'),
        premiumValue: pick('premiumValue'),
        straddlePremiumOperator: pick('straddlePremiumOperator'),
        straddlePremiumPercentage: pick('straddlePremiumPercentage'),
        straddleWidth: pick('straddleWidth'),
        underlyingPremiumOperator: pick('underlyingPremiumOperator'),
        underlyingPercentage: pick('underlyingPercentage'),
        stopLoss: n.stopLoss,
        targetProfit: n.targetProfit,
        trailingStopLoss: n.trailingStopLoss,
        waitAndTrade: n.waitAndTrade,
        reEntry: n.reEntry,
        reExecute: n.reExecute,
        isHedge: n.isHedge || false,
        hedgeForLegIndex,
        // Include the recycle opt-in so toggling it marks the strategy dirty (else an
        // unsaved recycle change would be invisible to the save-changes detector).
        hedgeRecycle: n.hedgeRecycle || false,
      };
    }),
  });

